import { useMemo } from "react";
import { ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ACTION_BUTTON_STYLE } from "../constants";

interface Props {
  content: string;
}

interface ExtractedLink {
  text: string;
  url: string;
}

function extractLinks(content: string): ExtractedLink[] {
  const links: ExtractedLink[] = [];
  const seen = new Set<string>();

  const markdownLinkRegex = /\[([^\]]*)\]\(([^)]+)\)/g;
  const plainUrlRegex = /(?<!\]\()https?:\/\/[^\s<>[\]()]+/g;

  let match;
  while ((match = markdownLinkRegex.exec(content)) !== null) {
    const url = match[2].trim();
    if (!seen.has(url)) {
      seen.add(url);
      links.push({
        text: match[1].trim() || url,
        url,
      });
    }
  }

  while ((match = plainUrlRegex.exec(content)) !== null) {
    const url = match[0].trim();
    if (!seen.has(url)) {
      seen.add(url);
      links.push({
        text: url,
        url,
      });
    }
  }

  return links;
}

export const SourcesButton = ({ content }: Props) => {
  const links = useMemo(() => extractLinks(content), [content]);

  if (links.length === 0) {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className={ACTION_BUTTON_STYLE}>
          <ExternalLink size={14} className="mr-1" />
          Sources ({links.length})
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Sources</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <ul className="space-y-3">
            {links.map((link, index) => (
              <li key={index} className="border-b border-gray-200 pb-3 last:border-0">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group -m-2 block rounded p-2 hover:bg-gray-50"
                >
                  <div className="flex items-start gap-2">
                    <ExternalLink size={14} className="mt-1 flex-shrink-0 text-gray-400 group-hover:text-blue-500" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 group-hover:text-blue-600">
                        {link.text}
                      </p>
                      {link.text !== link.url && <p className="mt-0.5 truncate text-xs text-gray-500">{link.url}</p>}
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};
